import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GradientBackground from '../components/GradientBackground';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <>
      <GradientBackground backgroundIndex={0} />
      <div style={{
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        zIndex: 1,
        overflow: 'hidden',
      }}>
        {/* Navigation */}
        <Navigation />

        {/* Terms of Service Content */}
        <section style={{
          minHeight: '100vh',
          padding: '80px 24px',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(20px)',
          position: 'relative',
        }}
        className="terms-of-service-section"
        >
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
          }}>
            {/* Back Button */}
            <button
              onClick={handleBackToHome}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                marginBottom: '40px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              {t('common.backToHome')}
            </button>

            {/* Terms of Service Content */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '24px',
              padding: '48px',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              backdropFilter: 'blur(20px)',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
            className="terms-of-service-content"
            >
              <h1 style={{
                fontSize: '36px',
                fontWeight: '600',
                color: 'white',
                marginBottom: '24px',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                textAlign: 'center',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
              className="terms-of-service-title"
              >
                {t('terms.title')}
              </h1>
              <p style={{
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.7)',
                marginBottom: '32px',
                textAlign: 'center',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              }}>
                {t('common.lastUpdated')}
              </p>

              <div style={{
                fontSize: '16px',
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: '1.7',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}>
                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px', 
                  marginTop: '32px',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  {t('terms.sections.acceptance.title')}
                </h2>
                <p style={{ marginBottom: '16px' }}>
                  {t('terms.sections.acceptance.text')}
                </p>

                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px', 
                  marginTop: '32px',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  {t('terms.sections.description.title')}
                </h2>
                <p style={{ marginBottom: '16px' }}>
                  {t('terms.sections.description.text')}
                </p>

                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px', 
                  marginTop: '32px',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  {t('terms.sections.userResponsibilities.title')}
                </h2>
                <p style={{ marginBottom: '16px' }}>
                  {t('terms.sections.userResponsibilities.text')}
                </p>
                <ul style={{ marginLeft: '20px', marginBottom: '16px' }}>
                  {(t('terms.sections.userResponsibilities.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>

                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px', 
                  marginTop: '32px',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  {t('terms.sections.content.title')}
                </h2>
                <p style={{ marginBottom: '16px' }}>
                  {t('terms.sections.content.text')}
                </p>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{t('terms.sections.content.moderation.label')}</strong> {t('terms.sections.content.moderation.text')}
                </p>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{t('terms.sections.content.availability.label')}</strong> {t('terms.sections.content.availability.text')}
                </p>

                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px', 
                  marginTop: '32px',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  {t('terms.sections.privacy.title')}
                </h2>
                <p style={{ marginBottom: '16px' }}>
                  {t('terms.sections.privacy.text')}
                </p>

                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px', 
                  marginTop: '32px',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  {t('terms.sections.serviceAvailability.title')}
                </h2>
                <p style={{ marginBottom: '16px' }}>
                  {t('terms.sections.serviceAvailability.text')}
                </p>

                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px', 
                  marginTop: '32px',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  {t('terms.sections.limitation.title')}
                </h2>
                <p style={{ marginBottom: '16px' }}>
                  {t('terms.sections.limitation.text')}
                </p>

                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px', 
                  marginTop: '32px',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  {t('terms.sections.changes.title')}
                </h2>
                <p style={{ marginBottom: '16px' }}>
                  {t('terms.sections.changes.text')}
                </p>

                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px', 
                  marginTop: '32px',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  {t('terms.sections.contact.title')}
                </h2>
                <p style={{ marginBottom: '16px' }}>
                  {t('terms.sections.contact.text')} <a 
                    href={`mailto:${t('terms.sections.contact.email')}`}
                    style={{
                      color: 'white',
                      textDecoration: 'none',
                    }}
                  >
                    {t('terms.sections.contact.email')}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default TermsOfService;
